import { useState, useEffect, useRef } from 'react';
import { WA_PHONE_B64 } from '../lib/constants';
import { WHATSAPP_LINKS } from '../lib/constants';
import { useLanguage } from '../context/LanguageContext';

// Reusable inline WhatsApp glyph — pass any Tailwind size/color classes
export const WhatsAppIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export const WhatsAppButton = ({ linkKey = 'community', text, className = '', children, testId }) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [armed, setArmed] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const mountedAt = useRef(Date.now());

  // Bots that auto-click on mount fail; arm the button after 600ms
  useEffect(() => {
    const id = setTimeout(() => setArmed(true), 600);
    return () => clearTimeout(id);
  }, []);

  const openModal = (e) => {
    e.preventDefault();
    if (!e.isTrusted) return;     // synthetic clicks blocked
    if (!armed) return;           // too-early click blocked
    setOpen(true);
  };

  const handleConfirm = (e) => {
    if (!e.isTrusted) return;
    if (honeypot) return;         // bot filled the trap
    if (Date.now() - mountedAt.current < 800) return;
    try {
      // If a direct invite/group URL is encoded (chat.whatsapp.com), open it as-is
      if (!text && WHATSAPP_LINKS[linkKey]) {
        const decoded = atob(WHATSAPP_LINKS[linkKey]);
        if (decoded.includes('chat.whatsapp.com')) {
          window.open(decoded, '_blank', 'noopener,noreferrer');
          setOpen(false);
          return;
        }
      }

      const phone = atob(WA_PHONE_B64);
      let msg = '';
      if (text) {
        msg = text;
      } else {
        const decoded = atob(WHATSAPP_LINKS[linkKey]);
        const match = decoded.match(/text=(.+)$/);
        msg = match ? decodeURIComponent(match[1]) : '';
      }
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      const encodedMsg = encodeURIComponent(msg);
      // Canonical WhatsApp click-to-chat URL — works on iOS/Android (opens app)
      // and desktop (opens WhatsApp Desktop if installed, otherwise web).
      const waMeUrl = `https://wa.me/${phone}?text=${encodedMsg}`;
      // Desktop-only fallback in case wa.me redirect is blocked
      const webUrl = `https://web.whatsapp.com/send?phone=${phone}&text=${encodedMsg}`;

      if (isMobile) {
        // Top-level navigation so iOS/Android trigger the universal link to WhatsApp
        window.location.href = waMeUrl;
      } else {
        window.open(waMeUrl, '_blank', 'noopener,noreferrer');
        // Belt-and-braces: if wa.me is somehow blocked, open web.whatsapp.com
        const fallbackTimer = setTimeout(() => {
          window.open(webUrl, '_blank', 'noopener,noreferrer');
        }, 1500);
        window.addEventListener('blur', () => clearTimeout(fallbackTimer), { once: true });
      }
    } catch {}
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className={className}
        data-testid={testId}
      >
        {children}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm animate-in fade-in"
          onClick={() => setOpen(false)}
          data-testid="wa-confirm-modal"
        >
          <div
            className="bg-white rounded-2xl p-6 md:p-8 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#5B7FA8]/15 flex items-center justify-center">
                <svg className="w-6 h-6 text-[#5B7FA8]" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-slate-900">{t.whatsappGate.title}</h3>
            </div>
            <p className="text-sm text-slate-600 mb-5 leading-relaxed">{t.whatsappGate.body}</p>

            {/* Honeypot — invisible to humans, eagerly filled by dumb bots */}
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
              name="website"
            />

            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-5 py-2.5 rounded-full text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                data-testid="wa-cancel-btn"
              >
                {t.whatsappGate.cancel}
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                className="px-5 py-2.5 rounded-full text-sm font-bold text-white bg-[#5B7FA8] hover:bg-[#4A6D95] transition-colors inline-flex items-center justify-center gap-2"
                data-testid="wa-confirm-btn"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                {t.whatsappGate.confirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// WhatsApp Community Section (replacing Newsletter)
